import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { useCompany, useCompanyId } from './useCompany';
import { catalog } from '../../api/organization';
import { companies } from '../../api/endpoints';
import { Button } from '../../components/Button';
import { Card, PageHeader } from '../../components/Surface';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field } from '../../components/Field';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { toast } from '../../components/Toast';
import { useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useSubmit } from '../auth/useSubmit';

/**
 * `13 §2`: the repository-name template and the execution defaults.
 *
 * The preview is the point of this screen. `repo_name_template` is validated
 * server-side against rules a user cannot be expected to hold in their head —
 * it must contain `{repo}`, only four variables exist, and what is left after
 * substitution has to be a legal GitHub name — so showing what a name will look
 * like beats explaining the rules.
 */
export function ReposSettingsScreen() {
  const t = useT();
  const id = useCompanyId();
  const company = useCompany();
  const queryClient = useQueryClient();
  const { busy, error, fields, submit } = useSubmit<void>();
  const models = useQuery({ queryKey: ['catalog', 'models'], queryFn: catalog.models });
  const [selectedEffort, setSelectedEffort] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const settings = company.data?.settings as Record<string, unknown> | undefined;
  const [template, setTemplate] = useState<string | null>(null);

  if (company.isPending) {
    return <SkeletonRows rows={2} />;
  }

  const current = template ?? String(settings?.repo_name_template ?? '{company}-{project}-{repo}');
  const preview = current
    .replace('{company}', company.data?.slug ?? 'acme')
    .replace('{project}', 'billing')
    .replace('{repo}', 'api')
    .replace('{year}', String(new Date().getFullYear()));

  const defaults = (settings?.defaults ?? {}) as Record<string, unknown>;

  const effortOptions = models.data?.data.find((model) => model.model === (selectedModel ?? defaults.llm_model))?.reasoning_efforts ?? ['low', 'medium', 'high', 'xhigh', 'max'];
  const effort = selectedEffort ?? String(defaults.reasoning_effort ?? 'medium');

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    void submit(async () => {
      await companies.updateSettings(id, {
        repo_name_template: current,
        defaults: {
          llm_model: String(form.get('llm_model') ?? ''),
          llm_provider: models.data?.data.find((model) => model.model === String(form.get('llm_model')))?.provider ?? String(defaults.llm_provider ?? 'anthropic'),
          reasoning_effort: String(form.get('reasoning_effort') ?? ''),
        },
      });

      await queryClient.invalidateQueries({ queryKey: ['company', id] });
      toast.success(t('settings.account.saved'));
    });
  };

  return (
    <form onSubmit={onSubmit} className="max-w-lg space-y-4">
      <PageHeader title={t('company.tabs.repos')} description={t('reposSettings.intro')} />

      {error ? <ErrorBanner error={error} /> : null}

      <Field
        label={t('reposSettings.template')}
        name="repo_name_template"
        value={current}
        onChange={(event) => setTemplate(event.target.value)}
        // Los nombres de variable son sintaxis de la plantilla: van tal cual en
        // los dos idiomas, porque es lo que hay que teclear.
        hint={t('reposSettings.templateHint', { vars: '{company}, {project}, {repo}, {year}', repo: '{repo}' })}
        error={fields.repo_name_template}
      />

      <Card tone="muted" size="sm">
        <p className="text-xs font-semibold text-ink-soft">{t('reposSettings.wouldBeNamed')}</p>
        <p className="font-mono text-sm text-ink">{preview}</p>
      </Card>

      <label className="block space-y-1 text-sm font-semibold">
        {t('reposSettings.defaultModel')}
        <select name="llm_model" className={cn(fieldStyles, 'border-line')} value={selectedModel ?? String(defaults.llm_model ?? 'claude-sonnet-5')} onChange={(event) => setSelectedModel(event.target.value)}>
          {!models.data?.data.some((model) => model.model === String(defaults.llm_model ?? 'claude-sonnet-5')) ? (
            <option value={String(defaults.llm_model ?? 'claude-sonnet-5')}>
              {/* El proveedor ya no lo sirve: se deja elegido para poder cambiarlo. */}
              {models.data ? t('profileSteps.capabilities.modelDeprecated', { model: String(defaults.llm_model ?? 'claude-sonnet-5') }) : String(defaults.llm_model ?? 'claude-sonnet-5')}
            </option>
          ) : null}
          {(models.data?.data ?? []).map((model) => <option key={model.model} value={model.model}>{model.provider} · {model.display_name}</option>)}
        </select>
      </label>

      <div className="space-y-1.5">
        <label htmlFor="reasoning_effort" className="block text-sm font-semibold text-ink">
          {t('reposSettings.defaultEffort')}
        </label>
        <select
          id="reasoning_effort"
          name="reasoning_effort"
          value={effortOptions.includes(effort) ? effort : 'high'}
          onChange={(event) => setSelectedEffort(event.target.value)}
          className={cn(fieldStyles, 'border-line')}
        >
          {effortOptions.map((level) => (
            <option key={level} value={level}>
              {t(`profileSteps.enums.effort.${level}` as Key)}
            </option>
          ))}
        </select>
        {fields['defaults.reasoning_effort'] ? (
          <p role="alert" className="animate-rise text-xs font-semibold text-danger">
            {fields['defaults.reasoning_effort']}
          </p>
        ) : null}
      </div>

      {/* Repositories are always private — a closed decision (`00 §3`), not a
          setting, so it is stated rather than offered. */}
      <p className="text-xs text-ink-faint">{t('reposSettings.alwaysPrivate')}</p>

      <Button type="submit" busy={busy}>
        {t('common.saveChanges')}
      </Button>
    </form>
  );
}
